import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { confirmBillMatch, rejectBillMatch } from '../services/api';
import { useError } from '../contexts/ErrorContext';

function formatMoney(cents) {
  if (cents == null) return '';
  const n = typeof cents === 'number' ? cents / 100 : Number(cents) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function payeeFromBillMatchAlertTitle(alert) {
  const t = alert?.title;
  if (typeof t === 'string' && t.startsWith('Bill match found: ')) {
    return t.slice('Bill match found: '.length);
  }
  return null;
}

const BillMatchReviewModal = ({ open, onClose, alert, onResolved }) => {
  const { showError, showSuccess } = useError();
  const [busyKey, setBusyKey] = useState(null);

  const candidates = useMemo(() => {
    const metadata = alert?.metadata || {};
    if (Array.isArray(metadata.candidates) && metadata.candidates.length > 0) {
      return metadata.candidates;
    }
    if (metadata.billId && metadata.transactionId) {
      return [
        {
          billId: metadata.billId,
          transactionId: metadata.transactionId,
          confidence: metadata.confidence,
          payee: metadata.payee || payeeFromBillMatchAlertTitle(alert),
          amountCents: metadata.amountCents,
        },
      ];
    }
    return [];
  }, [alert]);

  const alertId = alert?._id;

  const handleConfirm = useCallback(async (c) => {
    const key = `${c.billId}-${c.transactionId}-confirm`;
    setBusyKey(key);
    try {
      await confirmBillMatch(c.billId, c.transactionId, alertId);
      showSuccess('Match confirmed');
      onResolved?.();
    } catch (err) {
      showError(err?.response?.data?.error || 'Could not confirm match');
    } finally {
      setBusyKey(null);
    }
  }, [alertId, onResolved, showError, showSuccess]);

  const handleReject = useCallback(async (c) => {
    const key = `${c.billId}-${c.transactionId}-reject`;
    setBusyKey(key);
    try {
      await rejectBillMatch(c.billId, c.transactionId, alertId);
      showSuccess('Match dismissed');
      onResolved?.();
    } catch (err) {
      showError(err?.response?.data?.error || 'Could not dismiss match');
    } finally {
      setBusyKey(null);
    }
  }, [alertId, onResolved, showError, showSuccess]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <span>Review bill matches</span>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {candidates.length === 0 ? (
          <Typography color="text.secondary">No pending matches.</Typography>
        ) : (
          <List disablePadding>
            {candidates.map((c) => {
              const kConfirm = `${c.billId}-${c.transactionId}-confirm`;
              const kReject = `${c.billId}-${c.transactionId}-reject`;
              const loading = busyKey === kConfirm || busyKey === kReject;
              return (
                <ListItem
                  key={`${c.billId}-${c.transactionId}`}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 2,
                    py: 2,
                    px: 2
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600}>
                    {c.payee || payeeFromBillMatchAlertTitle(alert) || 'Bill'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatMoney(c.amountCents)}
                    {c.confidence != null ? ` · ${Math.round(Number(c.confidence) * 100)}% confidence` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      disabled={loading}
                      onClick={() => handleReject(c)}
                    >
                      {busyKey === kReject ? <CircularProgress size={18} /> : 'Not a match'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={loading}
                      onClick={() => handleConfirm(c)}
                    >
                      {busyKey === kConfirm ? <CircularProgress size={18} color="inherit" /> : 'Confirm'}
                    </Button>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BillMatchReviewModal;
